import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
    ArrowUpRight,
    ArrowRight,
    MapPin,
    CheckCircle2,
    PlayCircle,
    Sparkles,
} from "lucide-react";
import { services, stats, features, clientLogos } from "@/data/content";
import NetworkMap from "@/components/NetworkMap";
import ClientsSection from "@/components/ClientsSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import useSeo from "@/hooks/useSeo";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
    }),
};

export default function Home() {
    useSeo({
        title: null, // use the full default title on Home
        description:
            "Beeta Logistics is India's integrated 3PL partner — warehousing, transporting and auto-industry logistics. 28 branches, 6+ warehouses, 10,000+ operations since 2015. Trusted by 100+ clients including Yamaha, Honda, Tata Motors, Mahindra, Carrier and Zepto.",
        path: "/",
    });
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    // Truck drifts upward and slightly scales down as user scrolls
    const truckY = useTransform(scrollYProgress, [0, 1], [0, -140]);
    const truckScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
    const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.7, 0.95]);
    // Floating PAN-India badge moves slightly the opposite way for depth
    const badgeY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section ref={heroRef} className="relative overflow-hidden bg-white">
                <div className="absolute inset-0 bg-grid-slate opacity-40 pointer-events-none" />
                <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-blue-100/40 rounded-full blur-3xl pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-24">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7">
                            <motion.div
                                initial="hidden"
                                animate="show"
                                variants={fadeUp}
                                custom={0}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-8"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-yellow animate-pulse" />
                                <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                                <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                                    India&apos;s Integrated 3PL Partner
                                </span>
                            </motion.div>

                            <motion.h1
                                initial="hidden"
                                animate="show"
                                variants={fadeUp}
                                custom={1}
                                className="font-display text-4xl sm:text-6xl lg:text-7xl font-black leading-[1.08] sm:leading-[0.95] tracking-tighter text-slate-900"
                            >
                                Transportation
                                <br />
                                <span className="inline-flex flex-wrap items-center gap-2 sm:gap-3">
                                    <span className="relative inline-block">
                                        <span className="relative z-10">beyond</span>
                                        <span className="absolute left-0 right-0 bottom-1 h-2 bg-brand-yellow/70 -z-0 rounded-sm" />
                                    </span>
                                    <span className="relative inline-block">
                                        <span className="relative z-10 text-white px-2 sm:px-4">compare.</span>
                                        <span className="absolute inset-0 bg-blue-600 rounded-lg -rotate-1"></span>
                                    </span>
                                </span>
                            </motion.h1>

                            <motion.p
                                initial="hidden"
                                animate="show"
                                variants={fadeUp}
                                custom={2}
                                className="mt-7 text-lg text-slate-600 max-w-xl leading-relaxed"
                            >
                                Warehousing, transport and 3PL engineered into a single
                                accountable service. We run the network — you run your
                                business.
                            </motion.p>

                            <motion.div
                                initial="hidden"
                                animate="show"
                                variants={fadeUp}
                                custom={3}
                                className="mt-10 flex flex-wrap items-center gap-4"
                            >
                                <Link
                                    to="/quote"
                                    data-testid="hero-quote-cta"
                                    className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-slate-900 text-white font-semibold hover:bg-blue-600 transition-colors"
                                >
                                    Get Instant Quote
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </Link>
                                <Link
                                    to="/track"
                                    data-testid="hero-track-cta"
                                    className="group inline-flex items-center gap-2 px-7 py-4 rounded-full border border-slate-300 text-slate-800 font-semibold hover:bg-slate-50 transition-colors"
                                >
                                    <PlayCircle className="w-4 h-4" />
                                    Track Shipment
                                </Link>
                            </motion.div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="lg:col-span-5 relative"
                        >
                            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] bg-slate-900">
                                <motion.img
                                    src="https://images.unsplash.com/photo-1592838064575-70ed626d3a0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3MlMjB0cnVja3xlbnwwfHx8fDE3NzY0MTAwMTV8MA&ixlib=rb-4.1.0&q=85"
                                    alt="Beeta Logistics truck"
                                    style={{ y: truckY, scale: truckScale }}
                                    className="w-full h-full object-cover will-change-transform"
                                />
                                <motion.div
                                    style={{ opacity: overlayOpacity }}
                                    className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"
                                />

                                {/* Floating card */}
                                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-xl rounded-2xl p-5 shadow-xl">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="relative flex w-2.5 h-2.5">
                                            <span className="absolute inset-0 rounded-full bg-brand-yellow opacity-60 animate-ping" />
                                            <span className="relative w-2.5 h-2.5 rounded-full bg-brand-yellow" />
                                        </span>
                                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                            Live Telemetry
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-xs text-slate-500">Now serving</div>
                                            <div className="font-display text-2xl font-black text-slate-900">
                                                100+ clients
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500">Operations</div>
                                            <div className="font-display text-2xl font-black text-slate-900">
                                                10,000+
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating accent */}
                            <motion.div
                                style={{ y: badgeY }}
                                className="hidden md:flex absolute -left-6 top-12 bg-white rounded-2xl shadow-xl border border-slate-100 px-5 py-4 items-center gap-3"
                            >
                                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                                    <MapPin className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                        PAN-India
                                    </div>
                                    <div className="font-display font-bold text-slate-900 text-sm">
                                        20+ cities · 6+ WHs
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* LOGO MARQUEE */}
            <section className="bg-slate-50 border-y border-slate-200 py-10 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                        Trusted by India&apos;s leading OEMs, Tier-1s & industrial brands
                    </div>
                    <div className="text-xs font-semibold text-slate-400">
                        100+ active customers · Auto, industrial, e-commerce
                    </div>
                </div>
                <div className="relative">
                    {/* edge fades */}
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-slate-50 to-transparent z-10" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-slate-50 to-transparent z-10" />

                    <div className="flex animate-marquee whitespace-nowrap">
                        {[...clientLogos, ...clientLogos].map((logo, i) => (
                            <div
                                key={i}
                                className="mx-8 flex items-center justify-center shrink-0 h-10"
                                title={logo.name}
                            >
                                {logo.src ? (
                                    <img
                                        src={logo.src}
                                        alt={logo.name}
                                        loading="lazy"
                                        className="h-10 w-auto opacity-50 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-500"
                                    />
                                ) : (
                                    <span className="font-display font-bold text-xl tracking-tight text-slate-400 hover:text-slate-900 transition-colors">
                                        {logo.name}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="py-24 sm:py-32 bg-white" data-testid="stats-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-6">
                        {stats.map((s, i) => (
                            <motion.div
                                key={s.label}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="relative p-8 rounded-2xl border border-slate-200 bg-white hover:shadow-xl transition-all"
                            >
                                <div className="font-display text-6xl font-black text-slate-900 tracking-tighter">
                                    {s.value}
                                    <span className="text-brand-yellow">{s.suffix}</span>
                                </div>
                                <div className="mt-3 text-sm font-semibold text-slate-500 uppercase tracking-wider">
                                    {s.label}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES BENTO */}
            <section className="py-24 sm:py-32 bg-slate-50" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                                Our Services
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter max-w-2xl">
                                One partner. Every link in your chain.
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            data-testid="all-services-link"
                            className="inline-flex items-center gap-2 text-slate-900 font-semibold hover:gap-3 transition-all"
                        >
                            All services <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        {services.map((s, i) => {
                            const Icon = s.icon;
                            const isLarge = i === 0;
                            return (
                                <motion.div
                                    key={s.slug}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.06 }}
                                    className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white hover:shadow-2xl transition-all ${
                                        isLarge ? "md:col-span-6 md:row-span-2" : "md:col-span-6"
                                    }`}
                                >
                                    <Link
                                        to={`/services/${s.slug}`}
                                        data-testid={`service-card-${s.slug}`}
                                        className="block h-full"
                                    >
                                        <div className={`relative ${isLarge ? "aspect-[4/3]" : "aspect-[16/9]"} overflow-hidden`}>
                                            <img
                                                src={s.image}
                                                alt={s.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                                            <div className="absolute top-5 left-5 w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-lg">
                                                <Icon className="w-6 h-6 text-blue-600" strokeWidth={2} />
                                            </div>
                                        </div>
                                        <div className="p-7">
                                            <div className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600 mb-2">
                                                {s.tagline}
                                            </div>
                                            <h3 className="font-display text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                                                {s.title}
                                            </h3>
                                            <p className="text-slate-600 leading-relaxed">
                                                {s.summary}
                                            </p>
                                            {isLarge && (
                                                <>
                                                    <p className="mt-4 text-slate-600 leading-relaxed">
                                                        {s.description}
                                                    </p>
                                                    <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                                                        {s.highlights.map((h) => (
                                                            <li
                                                                key={h}
                                                                className="flex items-start gap-2 text-sm text-slate-700"
                                                            >
                                                                <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                                                                {h}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <div className="mt-7 grid grid-cols-3 divide-x divide-slate-200 rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
                                                        {[
                                                            { v: "100+", l: "Clients" },
                                                            { v: "168K", l: "Sqft Storage" },
                                                            { v: "20+", l: "Cities Served" },
                                                        ].map((k) => (
                                                            <div key={k.l} className="px-4 py-4 text-center">
                                                                <div className="font-display text-xl font-black text-slate-900 tracking-tight">
                                                                    {k.v}
                                                                </div>
                                                                <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mt-1 font-semibold">
                                                                    {k.l}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                            <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 group-hover:gap-2.5 transition-all">
                                                Explore <ArrowRight className="w-3.5 h-3.5" />
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* MAJOR CUSTOMERS */}
            <ClientsSection />

            {/* TESTIMONIALS */}
            <TestimonialsSection />

            {/* FEATURES */}
            <section className="py-24 sm:py-32 bg-white" data-testid="features-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                                Why Beeta
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter">
                                Built on operating discipline.
                            </h2>
                            <p className="mt-6 text-slate-600 leading-relaxed">
                                The difference between an average 3PL and a great one comes
                                down to what happens in the boring 80% — the routing, the
                                scans, the escalations. We&apos;re obsessed with that 80%.
                            </p>
                            <Link
                                to="/about"
                                className="mt-8 inline-flex items-center gap-2 text-slate-900 font-semibold hover:gap-3 transition-all"
                            >
                                The Beeta story <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
                            {features.map((f, i) => {
                                const Icon = f.icon;
                                return (
                                    <motion.div
                                        key={f.title}
                                        initial={{ opacity: 0, y: 24 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: i * 0.08 }}
                                        className="p-7 rounded-2xl border border-slate-200 bg-white hover:shadow-xl transition-all"
                                    >
                                        <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-5">
                                            <Icon className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <h3 className="font-display text-lg font-bold text-slate-900 mb-2">
                                            {f.title}
                                        </h3>
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            {f.body}
                                        </p>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* NETWORK MAP */}
            <NetworkMap />

            {/* PROCESS */}
            <section className="py-24 sm:py-32 bg-slate-900 text-white overflow-hidden relative">
                <div className="absolute inset-0 bg-dot-slate opacity-[0.06]" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">
                            How we onboard
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                            From first call to first dispatch — in 14 days.
                        </h2>
                    </div>

                    <div className="mt-16 grid md:grid-cols-4 gap-px bg-slate-800 rounded-3xl overflow-hidden">
                        {[
                            { n: "01", t: "Discovery", d: "Flows mapped, volumes baselined, cost drivers identified." },
                            { n: "02", t: "Design", d: "Service design, lane plan, WMS / TMS integration blueprint." },
                            { n: "03", t: "Pilot", d: "30-day controlled pilot on a live lane, with weekly reviews." },
                            { n: "04", t: "Scale", d: "Steady-state SLAs, monthly business reviews, continuous improvement." },
                        ].map((step, i) => (
                            <div key={step.n} className="p-8 bg-slate-900">
                                <div className="font-display text-6xl font-black text-blue-500 tracking-tighter mb-4">
                                    {step.n}
                                </div>
                                <div className="font-display text-xl font-bold mb-2">{step.t}</div>
                                <div className="text-sm text-slate-400 leading-relaxed">{step.d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 sm:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 p-12 md:p-20">
                        <div className="absolute inset-0 bg-dot-slate opacity-10" />
                        <div className="relative grid md:grid-cols-2 gap-10 items-center">
                            <div>
                                <h2 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tighter leading-tight">
                                    Let&apos;s move your cargo.
                                    <br />
                                    <span className="text-blue-200">Get a quote in 60 seconds.</span>
                                </h2>
                                <p className="mt-6 text-blue-100 max-w-md leading-relaxed">
                                    Fill a short form, see a live estimate and ETA — no account
                                    required.
                                </p>
                            </div>
                            <div className="flex flex-col gap-4 md:items-end">
                                <Link
                                    to="/quote"
                                    data-testid="cta-quote"
                                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-slate-900 font-bold hover:bg-slate-900 hover:text-white transition-colors"
                                >
                                    Get Instant Quote <ArrowUpRight className="w-4 h-4" />
                                </Link>
                                <div className="flex items-center gap-2 text-blue-100 text-sm">
                                    <CheckCircle2 className="w-4 h-4" />
                                    No credit card. No sales call. Just a number.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
